import { useCallback, useRef, useState } from 'react';

type QueuedUpdate = {
  productId: string;
  colorId: string;
  quantity: string;
  timestamp: number;
};

type UpdateHandler = (productId: string, colorId: string, quantity: string) => Promise<void>;

/**
 * Custom hook to handle debounced quantity updates with request cancellation.
 * Prevents race conditions when multiple cells are updated rapidly.
 */
export const useDebouncedQuantityUpdate = (
  updateHandler: UpdateHandler,
  debounceMs: number = 100,
  onStart?: () => void,
  onEnd?: () => void
) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingUpdatesRef = useRef<Map<string, QueuedUpdate>>(new Map());
  const activeRequestsRef = useRef<Set<string>>(new Set());
  const updateHandlerRef = useRef(updateHandler);

  // Keep the handler fresh without triggering re-renders or effect dependencies
  updateHandlerRef.current = updateHandler;

  const MAX_CONCURRENT_REQUESTS = 5;

  const processUpdates = useCallback(async () => {
    if (activeRequestsRef.current.size >= MAX_CONCURRENT_REQUESTS) {
      return;
    }

    const keys = Array.from(pendingUpdatesRef.current.keys());

    // If we have keys to process, ensure loading state is true
    if (keys.length > 0) {
      setIsUpdating((prev) => {
        if (!prev) onStart?.();
        return true;
      });
    } else if (activeRequestsRef.current.size === 0) {
      // If no keys and no active requests, we are done
      setIsUpdating((prev) => {
        if (prev) onEnd?.();
        return false;
      });
    }

    for (const key of keys) {
      // Stop spawning if we reached the limit
      if (activeRequestsRef.current.size >= MAX_CONCURRENT_REQUESTS) {
        break;
      }

      // Skip if there's already an active request for this cell
      if (activeRequestsRef.current.has(key)) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const update = pendingUpdatesRef.current.get(key);
      // eslint-disable-next-line no-continue
      if (!update) continue;

      pendingUpdatesRef.current.delete(key);
      activeRequestsRef.current.add(key);

      // Process independently to allow parallelism across different cells
      // and prevent blocking the loop
      (async () => {
        try {
          await updateHandlerRef.current(update.productId, update.colorId, update.quantity);
        } catch (error) {
          // Silent error - already handled in parent component
        } finally {
          activeRequestsRef.current.delete(key);

          // Check if we are completely done
          if (activeRequestsRef.current.size === 0 && pendingUpdatesRef.current.size === 0) {
            setIsUpdating((prev) => {
              if (prev) onEnd?.();
              return false;
            });
          } else {
            // Trigger another pass to handle any updates that were skipped or added
            // This ensures the queue keeps moving as slots free up
            processUpdates();
          }
        }
      })();
    }
  }, []);

  const scheduleUpdate = useCallback(
    (productId: string, colorId: string, quantity: string) => {
      const key = `${productId}-${colorId}`;

      // Store or update the pending request
      pendingUpdatesRef.current.set(key, {
        productId,
        colorId,
        quantity,
        timestamp: Date.now(),
      });

      // Set loading state immediately
      setIsUpdating(true);

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Schedule new batch processing
      timeoutRef.current = setTimeout(() => {
        processUpdates();
      }, debounceMs);
    },
    [debounceMs, processUpdates]
  );

  const flushUpdates = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    processUpdates();
  }, [processUpdates]);

  return {
    scheduleUpdate,
    flushUpdates,
    isUpdating,
  };
};
